import useAudio from "@/utils/hooks/useAudio";
import Valkyrie, {
  viArrowDownToLine,
  viArrowRotateRight,
  viPause,
  viPlay,
  viVolume0,
  viVolume1,
  viVolume2,
  viVolume3,
  viVolumeSlash,
} from "@sippy-platform/valkyrie";

import { Slider } from "@base-ui/react";
import { IconButton, Paper, Stack, Typography } from "@mui/material";

export default function Player({ src = "", id }: { src?: string; id?: string }) {
  const {
    playable,
    changeVolume,
    duration,
    isMuted,
    playPause,
    progress,
    secondsToTime,
    seek,
    seekFinish,
    status,
    toggleMute,
    toggleVolume,
    volume,
  } = useAudio(src);

  return (
    <Paper
      sx={[
        {
          containerType: "inline-size",
          overflow: "hidden",
          position: "relative",
          minWidth: 178,
          boxShadow: "none",
          p: 0.5,
          borderRadius: 1.5,
        },
      ]}
      variant="outlined"
      id={id}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "nowrap",
          gap: 0.5,
        }}
      >
        <IconButton onClick={playPause} disabled={!playable} size="small" color="inherit">
          <Valkyrie icon={status === "stopped" ? viArrowRotateRight : status === "paused" ? viPlay : viPause} />
        </IconButton>
        <Typography sx={{ textAlign: "center", fontSize: 14, px: 0.5, textWrap: "nowrap" }}>
          {secondsToTime(progress)}
          <span style={{ opacity: 0.75 }}> / {secondsToTime(duration)}</span>
        </Typography>
        <IconButton
          onClick={toggleVolume}
          size="small"
          color="inherit"
          sx={{
            display: "none",
            ["@container (max-width: 280px)"]: {
              display: "flex",
            },
          }}
          disabled={!playable}
        >
          <Valkyrie
            icon={
              isMuted || volume === 0
                ? viVolumeSlash
                : volume > 75
                  ? viVolume3
                  : volume > 50
                    ? viVolume2
                    : volume > 25
                      ? viVolume1
                      : viVolume0
            }
          />
        </IconButton>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 1,
            ["@container (max-width: 280px)"]: {
              display: "none",
            },
          }}
        >
          <IconButton onClick={toggleMute} size="small" color="inherit" disabled={!playable}>
            <Valkyrie
              icon={
                isMuted || volume === 0
                  ? viVolumeSlash
                  : volume > 75
                    ? viVolume3
                    : volume > 50
                      ? viVolume2
                      : volume > 25
                        ? viVolume1
                        : viVolume0
              }
            />
          </IconButton>

          <Slider.Root
            value={isMuted ? 0 : volume}
            onValueChange={changeVolume}
            min={0}
            max={100}
            thumbAlignment="edge"
          >
            <Slider.Control className="flex w-20 touch-none items-center select-none">
              <Slider.Track className="h-2 w-full rounded-full bg-zinc-200 select-none dark:bg-zinc-700">
                <Slider.Indicator className="rounded-s-full bg-[#0068d8] select-none" />
                <Slider.Thumb
                  aria-label="Volume"
                  className="h-4 w-2 rounded-full border-2 border-white bg-[#0068d8] select-none has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-zinc-950 dark:border-[#1f1f24] dark:has-focus-visible:outline-white"
                />
              </Slider.Track>
            </Slider.Control>
          </Slider.Root>
        </Stack>
        <IconButton href={src} size="small" color="inherit" disabled={!src} download>
          <Valkyrie icon={viArrowDownToLine} />
        </IconButton>
      </Stack>

      <Slider.Root
        value={progress}
        onValueChange={seek}
        onValueCommitted={seekFinish}
        min={0}
        max={duration}
        disabled={!playable}
        thumbAlignment="edge"
      >
        <Slider.Control className="group/track absolute inset-x-0 bottom-0 flex touch-none items-center select-none">
          <Slider.Track className="h-1 w-full select-none group-hover/track:bg-zinc-200 dark:group-hover/track:bg-zinc-700">
            <Slider.Indicator className="bg-[#0068d8] select-none" />
            <Slider.Thumb aria-label="Track progress" />
          </Slider.Track>
        </Slider.Control>
      </Slider.Root>
    </Paper>
  );
}
